import { UserAccountClient } from './../../../../../../base/models/models.swagger.generated';
import { forkJoin, Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfigurationFormCoordinatorService } from '../../configuration-form-coordinator.service';
import {
  ClientDTO,
  ExportInterfaceConfigurationDTO,
  ExportInterfaceConfigurationTransferType,
  ExportInterfaceConfigurationUserAccessInfoDTO,
  FilterDTO,
  FilterOperator,
  IExportInterfaceConfigurationUserAccessInfoDTO,
  Role,
  SortDirection,
  UpdateInterfaceUserInfoDTO,
  UpdateUserInfoDTO,
} from '../../../../../../base/models/models.swagger.generated';
import { ExportConfigurationService } from '../../../../export-configuration.service';
import { CommandModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { UserManagementService } from '../../../../../../base/user-management/user-management.service';
import { AuthService } from '../../../../../../base/services/auth.service';
import { CustomValidatorsFactory } from '../../../../../../base/shared/validators/custom-validators-factory';
import { passwordValidation } from '../../../../../../directives/password-validation.directive';
import { ErrorType } from '../../../../../../core/routing/error-types';
import { ToastService } from '../../../../../../base/services/toast.service';
import { UserEditFormType } from 'src/app/base/user-edit-form/user-edit-form-type';
import { UserEditFormComponent } from '../../../../../../base/user-edit-form/user-edit-form.component';

import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';

// internal helper structures to show email
interface IExportInterfaceConfigurationUserAccessInfoDTOWithEmail extends IExportInterfaceConfigurationUserAccessInfoDTO {
  email: string;
}

class ExportInterfaceConfigurationUserAccessInfoDTOWithEmail extends ExportInterfaceConfigurationUserAccessInfoDTO {
  public email: string;

  constructor(data: IExportInterfaceConfigurationUserAccessInfoDTOWithEmail) {
    super(data);
    this.email = data.email;
  }
}

@Component({
    selector: 'app-configuration-access',
    templateUrl: './configuration-access.component.html',
    styleUrls: ['./configuration-access.component.scss'],
    imports: [ GridModule,
               QueryBuilderModule,
               UserEditFormComponent,
               TranslateModule
    ]
})
export class ConfigurationAccessComponent extends GridBaseComponent<UserEditFormType> implements OnInit, OnDestroy {
  public get configurationFormGeneral(): UntypedFormGroup {
    return this.configurationFormService.configurationFormGeneral;
  }

  public get configuration(): ExportInterfaceConfigurationDTO {
    return this.configurationFormService.configurationDetailsDTO;
  }

  public get showEmail(): boolean {
    const transferType = this.configurationFormService.transferType;
    return transferType != ExportInterfaceConfigurationTransferType.FTPClient;
  }

  public get isUserAdminOrClientAdmin(): boolean {
    return this.authService.isUserAdminOrConfigurationAdmin();
  }

  public customEditSettings = {
    allowEditing: this.isUserAdminOrClientAdmin,
    mode: 'Dialog',
    showDeleteConfirmDialog: true,
  };

  @ViewChild(UserEditFormComponent)
  public userEditComponent: UserEditFormComponent;

  private subscriptions: Subscription = new Subscription();

  private allUsers: ExportInterfaceConfigurationUserAccessInfoDTOWithEmail[];

  constructor(translateService: TranslateService,
    private configurationFormService: ConfigurationFormCoordinatorService,
    private exportConfigurationService: ExportConfigurationService,
    private userManagementService: UserManagementService,
    private userAccountClient: UserAccountClient,
    private toastService: ToastService,
    private authService: AuthService,
    private fb: FormBuilder) {
    super(translateService, exportConfigurationService);
    this.gridCommands = [{
      type: 'Edit',
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-edit e-icons',
      },
    }] as CommandModel[];
  }

  ngOnInit() {
    this.initData();
    this.subscriptions.add(this.configurationFormService.configurationDetailsDTOUpdated.subscribe(data => {
      this.initData();
    }));
    this.subscriptions.add(this.configurationFormGeneral.controls.transferType.valueChanges.subscribe((v) => {
      setTimeout(() => {
        var users = this.getFilteredUsers();
        this.configurationFormGeneral.get('userAccessList').setValue(users);
        this.grid.refresh();
      }, 0);
    }));
    this.editDialogTitle = 'i18n.base.user-add-modal.titleEdit';
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }

  public initData(): void {
    let usersWithAccess = [];

    //Get all Users with access
    if (this.configurationFormGeneral.get('userAccessList').value != null) {
      usersWithAccess = this.configurationFormGeneral.get('userAccessList').value as ExportInterfaceConfigurationUserAccessInfoDTO[];
    }

    const transferType = this.configurationFormService.transferType;
    if (transferType != ExportInterfaceConfigurationTransferType.REST &&
      transferType != ExportInterfaceConfigurationTransferType.SFTPServer &&
      transferType != ExportInterfaceConfigurationTransferType.Email &&
      transferType != ExportInterfaceConfigurationTransferType.SFTPServerOnly) {
      this.configurationFormGeneral.get('userAccessList').setValue([]);
      return;
    }
    if (this.configuration != null) {
      //Get all Users
      this.exportConfigurationService.getAllAccessUsers(this.configuration.clientId).subscribe(data => {
        this.allUsers = [];
        data.forEach(user => {
          const exportInterfaceUser = new ExportInterfaceConfigurationUserAccessInfoDTOWithEmail({
            userId: user.userId,
            userName: user.userName,
            firstname: user.firstname,
            lastname: user.lastname,
            hasAccess: false,
            email: user.email,
          });
          //Check if user has access
          if (usersWithAccess.some(user => user.userId === exportInterfaceUser.userId && user.hasAccess === true)) {
            exportInterfaceUser.hasAccess = true;
          }
          this.allUsers.push(exportInterfaceUser);
        });
        var users = this.getFilteredUsers();
        this.configurationFormGeneral.get('userAccessList').setValue(users);
        this.grid.refresh();
      });
    }

  }

  public checkBoxChange(args) {
    const control = this.configurationFormGeneral.get('userAccessList');
    const selectedRowIndexes = args.selectedRowIndexes;
    // STARGATE-33 : Commented this part of the code since it is setting hasAccess to false for all users. args.selectedRowIndexes contains only those rows that are selected with current operation
    // const values: [] = control.value;
    // for (let i = 0; i < values.length; i++) {
    //   control.value[i].hasAccess = false;
    // }
    for (let i = 0; i < selectedRowIndexes.length; i++) {
      control.value[selectedRowIndexes[i]].hasAccess = true;
    }
  }

  public handleRowSelectingOrDeselecting(args) {
    if (!args.isInteracted) {
      // don't interfere with events that are not coming from a click - leads to initialization issues
      return;
    }
    const classList = args.target?.classList;
    if (classList?.length > 0) {
      for (const item of classList) {
        if (item == 'icon-edit') {
          // avoid checkbox value change if edit button was clicked
          args.cancel = true;
          return;
        }
      }
    }
  }

  // the following methods are needed since syncfusion is not firing checkBoxChange event when row is selected
  public rowsSelected(args) {
    if (!args.isInteracted) {
      // don't interfere with events that are not coming from a click - leads to initialization issues
      return;
    }
    this.selectionChanged(args.rowIndexes, true);
  }

  public rowsDeselected(args) {
    if (!args.isInteracted) {
      // don't interfere with events that are not coming from a click - leads to initialization issues
      return;
    }
    this.selectionChanged(args.rowIndexes, false);
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return undefined;
  }

  protected initializeEditForm(data: ExportInterfaceConfigurationUserAccessInfoDTOWithEmail): void {
    this.gridEditForm = this.fb.group<UserEditFormType>({
      userId: this.fb.control<string>(data.userId),
      firstname: this.fb.control<string>(null),
      lastname: this.fb.control<string>(null),
      language: this.fb.control<string>(null),
      email: this.fb.control<string>(null, [Validators.email]),
      phoneNumber: this.fb.control<string>(null),
      enabled: this.fb.control<boolean>(false),
      client: this.fb.control<ClientDTO>({ value: null, disabled: !!data.userId }),
      roles: this.fb.control<Role[]>([], Validators.required),
      userName: this.fb.control<string>({value: null, disabled: true}, [Validators.required, CustomValidatorsFactory.createNoWhiteSpacesAllowed()]),
      password: this.fb.control<string>(null, [passwordValidation(), CustomValidatorsFactory.createNoWhiteSpacesAllowed()])
    });

    let filters: FilterDTO[] = [];
    filters.push(new FilterDTO({ isComplex: false, field: 'UserId', operator: FilterOperator.Equal, value: data.userId.toString() }));
    forkJoin([this.userManagementService.getData(0, 0, filters, null, SortDirection.Ascending), this.userAccountClient.getServiceUserPassword(data.userId)]).subscribe({
      next: ([userInfoDtos, password]) => {
        const userInfoDto = userInfoDtos.items[0];
        this.gridEditForm.setValue({
          userId: userInfoDto.userId,
          firstname: userInfoDto.firstname,
          lastname: userInfoDto.lastname,
          language: userInfoDto.language,
          email: userInfoDto.email,
          phoneNumber: userInfoDto.phoneNumber,
          enabled: userInfoDto.enabled ?? false,
          client: userInfoDto.client,
          roles: !!userInfoDto.roles?.length ? userInfoDto.roles : [Role.InterfaceServiceAccess],
          userName: userInfoDto.userName,
          password: password
        });
        this.gridEditForm.updateValueAndValidity();
      },
      error: error => {
        this.grid.closeEdit();
        this.toastService.showError('i18n.core.messages.user-edit.title', 'i18n.core.messages.user-edit.message', null, ErrorType.serverError);
      }
    });
  }

  protected override getDataToSave(): any {
    this.exportConfigurationService.updateInterfaceUser(this.mapToUserInfoDTO(this.gridEditForm.getRawValue())).subscribe(() => {
      this.initData();
    });
  }

  private selectionChanged(rowIndexes: number[], value: boolean) {
    const control = this.configurationFormGeneral.get('userAccessList');
    for (let i = 0; i < rowIndexes?.length; i++) {
      control.value[rowIndexes[i]].hasAccess = value;
    }
  }

  private getFilteredUsers(): ExportInterfaceConfigurationUserAccessInfoDTOWithEmail[] {
    if (this.showEmail) {
      return this.allUsers?.filter(u => !!u.email);
    }
    return this.allUsers;
  }

  private mapToUserInfoDTO(formData: any): UpdateUserInfoDTO {
    let dto;
    dto = new UpdateInterfaceUserInfoDTO({
      userId: formData.userId,
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      enabled: formData.enabled,
      clientId: formData.client?.id,
      userName: formData.userName,
      password: formData.password,
      roles: formData.roles,
    });
    return dto;
  }
}
